import { useState } from "react";
import { toast } from "sonner";
import { FileText, CheckSquare } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { DOC_TYPES } from "../lib/constants";
import { mergedDocsPdfUrl } from "../lib/api";
import { triggerDownload } from "../lib/download";

export function MergedDocsExportDialog({ open, onOpenChange }) {
  const [selected, setSelected] = useState([]);

  const toggle = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const allKeys = DOC_TYPES.map((d) => d.key);
  const allSelected = selected.length === allKeys.length;
  const toggleAll = () => setSelected(allSelected ? [] : allKeys);

  const handleExport = () => {
    if (selected.length === 0) {
      toast.error("اختر نوع وثيقة واحد على الأقل");
      return;
    }
    // keep DOC_TYPES order
    const ordered = allKeys.filter((k) => selected.includes(k));
    const labels = DOC_TYPES.filter((d) => selected.includes(d.key)).map((d) => d.label);
    toast.info(`جارٍ تجميع (${labels.join("، ")}) لكل الموظفين النشطين...`);
    const fname =
      ordered.length === 1
        ? `${labels[0]}.pdf`
        : `وثائق مجمّعة - ${ordered.length} أنواع.pdf`;
    triggerDownload(mergedDocsPdfUrl(ordered, "active"), fname);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="font-body max-w-md" data-testid="merged-docs-dialog" dir="rtl">
        <DialogHeader className="text-right">
          <DialogTitle className="flex items-center gap-2 justify-start">
            <FileText className="h-5 w-5 text-indigo-600" /> تصدير وثيقة مجمّعة (PDF)
          </DialogTitle>
          <DialogDescription className="text-right">
            اختر نوعاً واحداً أو أكثر. سيتم تجميع ملفات الأنواع المحددة لكل الموظفين النشطين في ملف PDF واحد.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 py-1">
          <button
            type="button"
            data-testid="merged-docs-select-all"
            onClick={toggleAll}
            className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 mb-2"
          >
            <CheckSquare className="h-4 w-4" />
            {allSelected ? "إلغاء تحديد الكل" : "تحديد الكل"}
          </button>
          <div className="max-h-72 overflow-y-auto space-y-0.5 pr-1">
            {DOC_TYPES.map((d) => (
              <label
                key={d.key}
                data-testid={`merged-docs-opt-${d.key}`}
                className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-slate-50 cursor-pointer"
              >
                <Checkbox
                  checked={selected.includes(d.key)}
                  onCheckedChange={() => toggle(d.key)}
                  data-testid={`merged-docs-check-${d.key}`}
                />
                <span className="text-sm text-slate-700">{d.label}</span>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-start">
          <Button
            data-testid="merged-docs-export-confirm"
            onClick={handleExport}
            className="gap-1.5 bg-indigo-600 hover:bg-indigo-700"
            disabled={selected.length === 0}
          >
            <FileText className="h-4 w-4" /> تصدير ({selected.length})
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="merged-docs-cancel">
            إلغاء
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
